import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaGift, 
  FaSpinner, 
  FaCheck, 
  FaTimes, 
  FaPercentage, 
  FaTag,
  FaStar,
  FaTruck,
  FaFire,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import { discountApi } from '../utils/discountApi';
import { showSuccessNotification, showErrorNotification } from './NotificationManager';

const EnhancedDiscountSelector = ({
  orderAmount,
  restaurantId,
  deliveryFee = 600,
  orderItems = [],
  onDiscountApplied,
  onDiscountRemoved,
  disabled = false
}) => {
  const [discountCode, setDiscountCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [preview, setPreview] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [error, setError] = useState('');

  // Load discount suggestions when component mounts or order changes
  useEffect(() => {
    if (orderAmount > 0) {
      loadDiscountSuggestions();
    }
  }, [orderAmount, restaurantId]);

  // Preview discount when code changes
  const debouncedPreview = useCallback(
    debounce((code) => {
      if (code && code.length >= 3) {
        previewDiscount(code);
      } else {
        setPreview(null);
        setError('');
      }
    }, 500),
    [orderAmount, restaurantId, deliveryFee]
  );

  useEffect(() => {
    if (discountCode && !appliedDiscount) {
      debouncedPreview(discountCode);
    }
  }, [discountCode, debouncedPreview, appliedDiscount]);

  const loadDiscountSuggestions = async () => {
    try {
      const response = await discountApi.getDiscountSuggestions(orderAmount, restaurantId);
      if (response.success && response.suggestions) {
        setSuggestions(response.suggestions);
      }
    } catch (error) {
      console.error('Failed to load discount suggestions:', error);
    }
  };

  const previewDiscount = async (code) => {
    if (!code || appliedDiscount) return;

    setPreviewLoading(true);
    setError('');

    try {
      const response = await discountApi.previewDiscount({
        discountCode: code.toUpperCase(),
        orderAmount: orderAmount,
        restaurant: restaurantId,
        deliveryFee: deliveryFee
      });

      if (response.success && response.preview) {
        setPreview(response.preview);
        setError('');
      } else {
        setPreview(null);
        setError(response.message || 'Invalid discount code');
      }
    } catch (error) {
      setPreview(null);
      setError('Failed to validate discount code');
    } finally {
      setPreviewLoading(false);
    }
  };

  const applyDiscount = () => {
    if (preview && !preview.error && !appliedDiscount) {
      setAppliedDiscount(preview);
      setPreview(null);
      setShowSuggestions(false);
      onDiscountApplied(preview);
      showSuccessNotification(`${preview.discountName} applied! You save ₦${preview.savings.toLocaleString()}`);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setPreview(null);
    setError('');
    setShowSuggestions(true);
    onDiscountRemoved();
    showSuccessNotification('Discount removed');
  };

  const applySuggestion = (suggestion) => {
    setDiscountCode(suggestion.code);
    previewDiscount(suggestion.code);
  };

  const getDiscountIcon = (type) => {
    switch (type) {
      case 'percentage': return <FaPercentage className="text-green-600" />;
      case 'fixed': return <FaTag className="text-primary-600" />;
      case 'free_delivery': return <FaTruck className="text-purple-600" />;
      default: return <FaGift className="text-cheese" />;
    }
  };

  const getBadgeColor = (badge) => {
    switch (badge) {
      case 'Best Value': return 'bg-green-100 text-green-800 border-green-300';
      case 'Free Delivery': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'Popular': return 'bg-primary-100 text-primary-800 border-primary-300';
      default: return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-secondary-50 to-primary-50 rounded-xl p-6 border-2 border-pepperoni/20"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <FaGift className="text-cheese text-xl" />
          <h3 className="text-lg font-bold text-crust font-display">Discounts & Offers</h3>
        </div>
        {appliedDiscount && (
          <div className="flex items-center gap-2 text-green-600">
            <FaCheck className="text-sm" />
            <span className="text-sm font-semibold">Applied</span>
          </div>
        )}
      </div>

      {/* Applied Discount Display */}
      {appliedDiscount && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-green-500 rounded-full p-2">
                <FaCheck className="text-white text-sm" />
              </div>
              <div>
                <h4 className="font-bold text-green-800 font-display">{appliedDiscount.discountName}</h4>
                <p className="text-sm text-green-600 font-sans">
                  Code: <span className="font-bold">{appliedDiscount.discountCode}</span>
                </p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={removeDiscount}
              className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50"
              title="Remove discount"
            >
              <FaTimes />
            </motion.button>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <FaPercentage className="text-xs" />
                <span className="text-sm font-semibold font-display">You Save</span>
              </div>
              <p className="text-xl font-bold text-green-800 font-display">₦{appliedDiscount.savings.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <div className="text-green-600 text-sm font-semibold font-display mb-1">New Total</div>
              <p className="text-xl font-bold text-green-800 font-display">₦{appliedDiscount.finalAmount.toLocaleString()}</p>
            </div>
          </div>

          {appliedDiscount.termsAndConditions && (
            <div className="mt-3 p-2 bg-green-100 rounded-lg">
              <p className="text-xs text-green-700 font-sans">{appliedDiscount.termsAndConditions}</p>
            </div>
          )}
        </motion.div>
      )}

      {/* Discount Input and Suggestions */}
      {!appliedDiscount && (
        <>
          {/* Smart Suggestions */}
          {suggestions.length > 0 && showSuggestions && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-crust font-display flex items-center gap-2">
                  <FaStar className="text-cheese text-sm" />
                  Available for You
                </h4>
                <button
                  onClick={() => setShowSuggestions(!showSuggestions)}
                  className="text-pepperoni hover:text-primary-800 text-sm"
                >
                  {showSuggestions ? <FaChevronUp /> : <FaChevronDown />}
                </button>
              </div>
              
              <div className="space-y-3">
                {suggestions.slice(0, 3).map((suggestion, index) => (
                  <motion.div
                    key={suggestion.code || `suggestion-${index}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-lg p-4 border-2 border-secondary-200 hover:border-cheese transition-all cursor-pointer"
                    onClick={() => applySuggestion(suggestion)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getDiscountIcon(suggestion.type)}
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h5 className="font-bold text-crust font-display">{suggestion.name}</h5>
                            {suggestion.badge && (
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getBadgeColor(suggestion.badge)}`}>
                                {suggestion.badge}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-olive-light font-sans">{suggestion.description}</p>
                          <p className="text-xs text-pepperoni font-semibold mt-1">
                            Code: {suggestion.code}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-600 font-display">
                          ₦{suggestion.potentialSavings.toLocaleString()}
                        </div>
                        <div className="text-xs text-olive-light">savings</div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Manual Discount Input */}
          <div>
            <h4 className="text-sm font-bold text-crust font-display mb-3 flex items-center gap-2">
              <FaTag className="text-pepperoni text-sm" />
              Enter Discount Code
            </h4>
            
            <div className="flex gap-2 mb-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Enter discount code"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  disabled={disabled || loading}
                  className="w-full p-3 border border-secondary-400 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans text-center font-bold tracking-wider"
                />
              </div>
              {preview && !preview.error && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={applyDiscount}
                  disabled={disabled || loading}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Apply
                </motion.button>
              )}
            </div>

            {/* Preview Loading */}
            {previewLoading && (
              <div className="flex items-center justify-center py-4">
                <FaSpinner className="animate-spin text-pepperoni mr-2" />
                <span className="text-sm text-olive-light">Validating discount...</span>
              </div>
            )}

            {/* Discount Preview */}
            <AnimatePresence>
              {preview && !previewLoading && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4"
                >
                  {preview.error ? (
                    <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-red-600">
                        <FaTimes className="text-red-500" />
                        <span className="font-semibold">Invalid Discount</span>
                      </div>
                      <p className="text-sm text-red-600 mt-1">{preview.error}</p>
                    </div>
                  ) : (
                    <div className="bg-primary-50 border-2 border-primary-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-primary-600 mb-3">
                        <FaCheck className="text-primary-500" />
                        <span className="font-semibold font-display">Preview: {preview.discountName}</span>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Original Amount:</span>
                          <span>₦{preview.originalAmount.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-primary-600">
                          <span>Discount ({preview.discountType}):</span>
                          <span>-₦{preview.discountAmount.toLocaleString()}</span>
                        </div>
                        <div className="border-t border-primary-200 pt-2 flex justify-between font-bold">
                          <span>Final Amount:</span>
                          <span className="text-primary-700">₦{preview.finalAmount.toLocaleString()}</span>
                        </div>
                      </div>
                      
                      <div className="mt-3 p-2 bg-primary-100 rounded-lg">
                        <p className="text-xs text-primary-700 font-sans flex items-center gap-1">
                          <FaFire className="text-primary-500" />
                          You'll save ₦{preview.savings.toLocaleString()} with this discount!
                        </p>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {error && !preview && !previewLoading && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-4"
                >
                  <div className="flex items-center gap-2 text-red-600">
                    <FaTimes className="text-red-500" />
                    <span className="font-semibold">Error</span>
                  </div>
                  <p className="text-sm text-red-600 mt-1">{error}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </>
      )}
    </motion.div>
  );
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default EnhancedDiscountSelector;